import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../api/endpoints';
import { fileUrl, uploadDirect } from '../../api/client';
import PageHeader from '../../components/PageHeader';
import ImageUploader from '../../components/ImageUploader';
import OnboardingQuestions, { missingRequired } from '../../components/OnboardingQuestions';
import { Button, Chip, Input } from '../../components/Ui';
import { Reveal } from '../../components/Motion';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { EDUCATION_LEVELS } from '../../constants';
import { colors } from '../../theme';

const levelLabel = (v) => (EDUCATION_LEVELS.find((l) => l.value === v) || {}).label || 'Other';
const BLANK_EDU = { level: '10th', course: '', institute: '', passingYear: '', proof: '' };

/** One file (resume, letter, certificate) straight to storage - PDFs included. */
const FileField = ({ label, hint, value, onChange, folder = 'resume' }) => {
  const toast = useToast();
  const [progress, setProgress] = useState(null);
  const choose = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    setProgress(0);
    try { onChange(await uploadDirect(file, folder, setProgress)); } catch (err) { toast.error(err.message); } finally { setProgress(null); e.target.value = ''; }
  };
  return (
    <div style={{ marginBottom: 14, display: 'grid', gap: 6 }}>
      <div className="t-small" style={{ fontWeight: 700 }}>{label}</div>
      <div className="row" style={{ gap: 10, flexWrap: 'wrap' }}>
        <label className="btn ghost" style={{ cursor: progress === null ? 'pointer' : 'progress' }}>
          {progress !== null ? `Uploading ${Math.round(progress * 100)}%` : value ? 'Replace file' : '📄 Choose file'}
          <input type="file" accept=".pdf,.doc,.docx,image/*" hidden onChange={choose} />
        </label>
        {value ? (
          <>
            <a className="btn ghost" href={fileUrl(value)} target="_blank" rel="noreferrer">Open</a>
            <button type="button" className="link-btn" style={{ color: 'var(--danger)' }} onClick={() => onChange('')}>Remove</button>
          </>
        ) : null}
      </div>
      {hint ? <div className="t-tiny">{hint}</div> : null}
    </div>
  );
};

/**
 * "Open to work" profile, for customers and partners alike: availability,
 * resume, experience, letters from past jobs and education with passing years.
 */
const WorkProfilePage = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const { user, setUser } = useAuth();
  const [form, setForm] = useState({
    lookingForJob: Boolean(user && user.lookingForJob),
    avatar: (user && user.avatar) || '',
    name: (user && user.name) || '',
    jobTitle: (user && user.jobTitle) || '',
    experienceYears: String((user && user.experienceYears) || ''),
    skills: ((user && user.skills) || []).join(', '),
    resume: (user && user.resume) || '',
    experienceLetters: (user && user.experienceLetters) || [],
    education: ((user && user.education) || []).map((e) => ({ ...e, passingYear: e.passingYear ? String(e.passingYear) : '' })),
  });
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  /*
   * Admin decides what this form asks: which built-in sections appear, what
   * they are called, and any extra questions of their own.
   */
  const [fields, setFields] = useState(null);
  const [answers, setAnswers] = useState(() => Object.fromEntries(
    (((user && user.workAnswers) || [])).map((a) => [a.key, { value: a.value, files: a.files || [] }]),
  ));
  useEffect(() => {
    api.onboarding.fields({ form: 'job_seeker' }).then((r) => setFields(r.data || [])).catch(() => setFields([]));
  }, []);
  const block = (key) => (fields === null ? {} : (fields.find((f) => f.builtin === key) || null));
  const shows = (key) => block(key) !== null;
  const labelFor = (key, fallback) => (block(key) || {}).label || fallback;
  const hintFor = (key, fallback) => (block(key) || {}).hint || fallback;
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const save = async (e) => {
    e.preventDefault();
    const missing = form.lookingForJob ? missingRequired((fields || []).filter((f) => f.kind !== 'builtin'), answers) : null;
    if (missing) { toast.error(`Please add: ${missing}`, 'One more thing'); return; }
    setSaving(true);
    try {
      const res = await api.user.update({
        avatar: form.avatar,
        name: form.name.trim(),
        lookingForJob: form.lookingForJob,
        jobTitle: form.jobTitle.trim(),
        experienceYears: Number(form.experienceYears) || 0,
        skills: form.skills.split(',').map((s) => s.trim()).filter(Boolean),
        resume: form.resume,
        experienceLetters: form.experienceLetters,
        education: form.education.map((ed) => ({ ...ed, passingYear: Number(ed.passingYear) || undefined })),
        workAnswers: answers,
      });
      setUser(res.data);
      toast.success(form.lookingForJob
        ? 'Employers hiring on KAAMWALA can now see that you are open to work.'
        : 'Your work profile is saved.', 'Saved');
      navigate('/account');
    } catch (err) {
      toast.error(err.message, 'Could not save');
    } finally { setSaving(false); }
  };

  return (
    <div style={{ maxWidth: 760 }}>
      <PageHeader title="Job seeker" subtitle="Your profile employers see" back />

      <Reveal className="card sh-sm">
        <form onSubmit={save}>
          <div className="row-between" style={{ gap: 12, background: colors.bg, borderRadius: 12, padding: 12, marginBottom: 16 }}>
            <div>
              <div className="t-title">I am looking for a job</div>
              <div className="t-tiny">Employers posting jobs can see your profile and call you.</div>
            </div>
            <label className="row" style={{ gap: 8, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={form.lookingForJob}
                onChange={(e) => set({ lookingForJob: e.target.checked })}
                style={{ width: 18, height: 18, accentColor: colors.primary }}
              />
              <span className="t-small">{form.lookingForJob ? 'Open to work' : 'Not looking'}</span>
            </label>
          </div>

          <ImageUploader
            label="Your photo"
            hint="Employers see this next to your name. A clear face photo works best."
            folder="avatar"
            multiple={false}
            value={form.avatar}
            onChange={(v) => set({ avatar: v })}
          />
          <Input label="Your name" value={form.name} onChange={(e) => set({ name: e.target.value })} />
          {shows('jobTitle') ? (
            <Input label={labelFor('jobTitle', 'What work do you do?')} hint={hintFor('jobTitle', '')}
              placeholder="e.g. Electrician, Delivery boy, Accountant"
              value={form.jobTitle} onChange={(e) => set({ jobTitle: e.target.value })} />
          ) : null}
          {shows('experienceYears') ? (
            <Input label={labelFor('experienceYears', 'Total experience (years)')} inputMode="numeric" value={form.experienceYears}
              onChange={(e) => set({ experienceYears: e.target.value.replace(/\D/g, '') })} />
          ) : null}
          {shows('skills') ? (
            <Input label={labelFor('skills', 'Your skills')} hint={hintFor('skills', 'Separate skills with commas')}
              placeholder="Wiring, plumbing, billing…"
              value={form.skills} onChange={(e) => set({ skills: e.target.value })} />
          ) : null}

          {shows('resume') ? (
            <FileField label={labelFor('resume', 'Resume')}
              hint={hintFor('resume', 'PDF, Word file or a photo. Employers open it from your application.')}
              value={form.resume} onChange={(v) => set({ resume: v })} />
          ) : null}

          {shows('experienceLetters') ? (
            <ImageUploader
              label={labelFor('experienceLetters', 'Experience letters (optional)')}
              hint={hintFor('experienceLetters', 'Letters or certificates from places you worked before.')}
              folder="document"
              max={5}
              value={form.experienceLetters}
              onChange={(v) => set({ experienceLetters: v })}
            />
          ) : null}

          {shows('education') ? (
            <>
          <div className="t-small" style={{ fontWeight: 700, margin: '18px 0 8px' }}>{labelFor('education', 'Education')}</div>
          {form.education.map((ed, i) => (
            <div key={`${ed.level}-${i}`} className="row-between" style={{ gap: 12, border: `1px solid ${colors.line}`, borderRadius: 12, padding: 12, marginBottom: 8 }}>
              <div>
                <div className="t-title">{levelLabel(ed.level)}{ed.course ? ` · ${ed.course}` : ''}</div>
                <div className="t-tiny">
                  {[ed.institute, ed.passingYear ? `Passed ${ed.passingYear}` : ''].filter(Boolean).join(' · ') || 'No details'}
                </div>
                {ed.proof ? <a className="t-tiny" href={fileUrl(ed.proof)} target="_blank" rel="noreferrer">📎 Certificate</a> : null}
              </div>
              <button type="button" className="link-btn" style={{ color: 'var(--danger)' }}
                onClick={() => set({ education: form.education.filter((_, x) => x !== i) })}>
                Remove
              </button>
            </div>
          ))}

          {draft ? (
            <div style={{ border: `1px solid ${colors.line}`, borderRadius: 12, padding: 12, marginBottom: 8 }}>
              <div className="t-small" style={{ fontWeight: 700, marginBottom: 6 }}>What did you study?</div>
              <div className="row wrap" style={{ gap: 8, marginBottom: 10 }}>
                {EDUCATION_LEVELS.map((l) => (
                  <Chip key={l.value} label={l.label} active={draft.level === l.value} onClick={() => setDraft({ ...draft, level: l.value })} />
                ))}
              </div>
              <Input label="Course or board (optional)" placeholder="e.g. B.A. History, CBSE"
                value={draft.course} onChange={(e) => setDraft({ ...draft, course: e.target.value })} />
              <Input label="School or college (optional)" value={draft.institute}
                onChange={(e) => setDraft({ ...draft, institute: e.target.value })} />
              <Input label="Passing year" inputMode="numeric" maxLength={4} placeholder="e.g. 2019"
                value={draft.passingYear} onChange={(e) => setDraft({ ...draft, passingYear: e.target.value.replace(/\D/g, '').slice(0, 4) })} />
              <FileField label="Marksheet or certificate (optional)" folder="document"
                value={draft.proof} onChange={(v) => setDraft({ ...draft, proof: v })} />
              <div className="row" style={{ gap: 10 }}>
                <Button title="Add" onClick={() => { set({ education: [...form.education, draft] }); setDraft(null); }} />
                <Button title="Cancel" variant="light" onClick={() => setDraft(null)} />
              </div>
            </div>
          ) : (
            <button type="button" className="btn ghost" style={{ borderStyle: 'dashed' }} onClick={() => setDraft({ ...BLANK_EDU })}>
              ＋ Add education
            </button>
          )}
            </>
          ) : null}

          {/* Anything else admin asks for on this form. */}
          <OnboardingQuestions form="job_seeker" fields={fields || []} answers={answers} onChange={setAnswers} />

          <Button title="Save work profile" type="submit" size="lg" full loading={saving} style={{ marginTop: 20 }} />
        </form>
      </Reveal>
    </div>
  );
};

export default WorkProfilePage;
